import "./App.css";
import AllRoutes from "./AllRoutes/AllRoutes";
import useLanguageTranslate from "./hooks/useLanguageTranslate";
import useLanguagePercistant from "./hooks/useLanguagePercistant";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchLoginRegisterContent,
  loginUser,
  selectLoginTokenInfo,
} from "./redux/features/loginRegisterSlice";
import { fetchCommnApiContent } from "./redux/features/commonApiDataSlice";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { fetchPoliciesBg } from "./redux/features/myPolicySlice";
import { BrowserRouter } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { fetchNotifications } from "./redux/features/notificationsSlice";
import useScreenResolution from "./hooks/useScreenResolution";
import { toggleSidenav } from "./redux/features/toolsSlice";
import { fetchFaqs } from "./redux/features/faqsSlice";
import { useTranslation } from "react-i18next";
import useTokenRefresh from "./hooks/useTokenRefresh";
import usePushNotifications from "./hooks/usePushNotifications";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import Gleap from "gleap";
import GleapReinitWatcher from "./GleapReinitWatcher";

// Floor between two resume-triggered inbox reads. Switching apps on a phone
// fires visibilitychange constantly, and this is a badge count, not a feed.
const RESUME_REFRESH_MIN_INTERVAL_MS = 30 * 1000;

function App() {
  posthog.init("phc_Io7V4sreA3fhEQ5UPXubx8ag5WV8NOFFzd0pUcIX6wJ", {
    api_host: "https://eu.i.posthog.com",
  });

  Sentry.init({
    dsn: "https://7a892d3ce354fef8019f6290509fda07@o432102.ingest.us.sentry.io/4509185093074944",
    integrations: [
      posthog.sentryIntegration({
        projectId: "4509185093074944",
        severityAllowList: ["error", "info"], // optional: here is set to handle captureMessage (info) and captureException (error)
      }),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.8,
  });

  useLanguagePercistant();
  useTokenRefresh();
  usePushNotifications();
  const translate = useLanguageTranslate();
  const { tablet, mobile } = useScreenResolution();
  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  useEffect(() => {
    dispatch(fetchLoginRegisterContent());
    dispatch(fetchCommnApiContent());
    dispatch(fetchFaqs());
    document.body.dir = translate("ltr", "rtl");
    // Gleap disabled for now
    // Gleap.getInstance().softReInitialize();
  }, []);

  // Collapse the side rail when the viewport enters the tablet band, so an iPad
  // does not hand a quarter of its width to navigation. This used to run only
  // on mount, which meant rotating an iPad into portrait kept the expanded rail
  // over the content; it now reacts to the breakpoint itself.
  //
  // Deliberately one-way: entering tablet collapses, leaving it does not
  // re-expand. Auto-expanding would undo a collapse the user chose, and the
  // header's toggle is available at every width the rail is.
  useEffect(() => {
    if (tablet) {
      dispatch(toggleSidenav({ open: false }));
    }
  }, [tablet, dispatch]);
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  useEffect(() => {
    dispatch(fetchPoliciesBg());
  }, [loginTokenInfo]);

  // Warms the inbox on sign-in so the panel opens with content rather than a
  // skeleton. The panel fetches on open too, and push arrivals refresh it (see
  // hooks/usePushNotifications.js) — this just means the first open is instant.
  useEffect(() => {
    if (!loginTokenInfo?.access_token) return;
    dispatch(fetchNotifications());
  }, [dispatch, loginTokenInfo?.access_token]);

  // The inbox's third read point: app resume. A push that arrives while the tab
  // is hidden is rendered by the service worker, which has no way to reach the
  // store, so the cached list goes stale the moment one lands. The row is
  // already in /mine, so re-read it rather than trying to thread the payload
  // back in from the worker.
  useEffect(() => {
    if (!loginTokenInfo?.access_token) return undefined;
    // The mount effect above has just fetched, so start the clock now.
    let lastFetchedAt = Date.now();
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastFetchedAt < RESUME_REFRESH_MIN_INTERVAL_MS) return;
      lastFetchedAt = Date.now();
      dispatch(fetchNotifications());
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [dispatch, loginTokenInfo?.access_token]);

  useEffect(() => {
    document.body.dir = translate("ltr", "rtl");
  }, [i18n.resolvedLanguage]);

  return (
    <div className={translate("english", "arabic")}>
      <Toaster mobile={mobile} dir={translate("ltr", "rtl")} />
      <BrowserRouter>
        <GleapReinitWatcher />
        <AllRoutes />
      </BrowserRouter>
    </div>
  );
}

export default App;
